import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Dialog, DialogTitle, DialogContent, Card, CardContent, Typography, Stack, Box, Chip, Avatar, IconButton, Tooltip, Divider, CircularProgress, Grid } from '@mui/material'
import { EditOutlined, CloseOutlined, FileTextOutlined } from '@ant-design/icons'
import StringAvatar from 'components/common/StringAvatar'
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { getPermissionKeys } from 'utils/util'
import { useApiData } from 'services/hook/useApiData'
import { formatDate } from 'utils/helpers'
import { EVENT_STOCKUNIT_DETAIL_INIT, EVENT_STOCKUNIT_UPDATE_INIT, EVENT_STOCKUNIT_UPDATE_SUCCESS, } from 'models/events/stockUnit.event'
import { stockUnitDetail } from 'models/stockUnit'

export const StockUnitDetailDialog = ({ isEdit = true }) => {
    const [stockUnitId, setStockUnitId] = useState(null)
    const eventbus = useEventBusContext()

    useEffect(() => {
        return eventbus.on(EVENT_STOCKUNIT_DETAIL_INIT, (item) => {
            setStockUnitId(item.id)
        })
    }, [eventbus])

    return (
        <Dialog
            open={stockUnitId != null}
            maxWidth="md"
            fullWidth
            scroll="body"
            onClose={() => setStockUnitId(null)}
            PaperProps={{
                sx: {
                    borderRadius: 3
                }
            }}
        >
            {stockUnitId && (
                <StockUnitDetail
                    stockUnitId={stockUnitId}
                    isEdit={isEdit}
                    onClose={() => setStockUnitId(null)}
                />
            )}
        </Dialog>
    )
}

const StockUnitDetail = ({ stockUnitId, isEdit, onClose }) => {
    const eventbus = useEventBusContext()
    const permissionsKeys = getPermissionKeys('stock_unit')

    const [data, , loading, loadData] = useApiData(
        { data: {} },
        {},
        (params, setData) => {
            if (!stockUnitId) {
                setData({})
                return
            }

            stockUnitDetail(stockUnitId)
                .then((res) => {
                    setData(res.data || res)
                })
                .catch(() => {
                    setData({})
                })
        }
    )

    useEffect(() => {
        return eventbus.on(EVENT_STOCKUNIT_UPDATE_SUCCESS, () => {
            loadData()
        })
    }, [eventbus, loadData])

    useEffect(() => {
        if (stockUnitId) {
            loadData()
        }
    }, [stockUnitId, loadData])

    const DetailRow = ({ label, value }) => (
        <Grid item xs={12} sm={6}>
            <Box
                sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 2
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        textTransform: 'uppercase',
                        fontWeight: 600
                    }}
                >
                    {label}
                </Typography>

                <Typography
                    variant="h6"
                    sx={{
                        mt: 0.5,
                        textTransform: 'capitalize'
                    }}
                >
                    {value || '-'}
                </Typography>
            </Box>
        </Grid>
    )

    if (loading) {
        return (
            <>
                <DialogTitle>Loading...</DialogTitle>

                <DialogContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 250
                        }}
                    >
                        <CircularProgress />
                    </Box>
                </DialogContent>
            </>
        )
    }

    if (!data?.name) {
        return (
            <>
                <DialogTitle>Stock Unit Not Found</DialogTitle>

                <DialogContent>
                    <Typography color="error">
                        Unable to load Stock Unit.
                    </Typography>
                </DialogContent>
            </>
        )
    }

    return (
        <>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center" >
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ position: 'relative' }}>
                            <StringAvatar
                                text={data.name}
                                size="large"
                            />

                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -4,
                                    right: -4,
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: data.status === 'active' ? 'success.main' : 'error.main', border: '2px solid white'
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="h4">{data.name}</Typography>

                            <Chip label={data.status === 'active' ? 'Active' : 'Inactive'} color={data.status === 'active' ? 'success' : 'error'} size="small" sx={{ mt: 1 }} />
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        {permissionsKeys.can_update && isEdit && (
                            <Tooltip title="Edit Stock Unit">
                                <IconButton color="primary"
                                    onClick={() => {
                                        onClose()
                                        eventbus.fire(EVENT_STOCKUNIT_UPDATE_INIT, categoryId)
                                    }}
                                    size="large"
                                >
                                    <EditOutlined />
                                </IconButton>
                            </Tooltip>
                        )}

                        <IconButton onClick={onClose} size="large">
                            <CloseOutlined />
                        </IconButton>
                    </Stack>

                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ mt: 2 }}>
                <Card>
                    <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" mb={3} >
                            <FileTextOutlined />

                            <Typography variant="h6">Stock Unit Information</Typography>
                        </Stack>

                        <Grid container spacing={2}>
                            <DetailRow label="Name" value={data.name} />

                            <DetailRow label="Status" value={data.status} />

                            <DetailRow label="Created At" value={data.created_at ? formatDate(data.created_at) : '-'} />

                            <DetailRow label="Updated At" value={data.updated_at ? formatDate(data.updated_at) : '-'} />
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
        </>
    )
}

StockUnitDetail.propTypes = {
    stockUnitId: PropTypes.number.isRequired,
    isEdit: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
}

export default StockUnitDetailDialog